import isNil from 'lodash/isNil';
import {
  ICriteriaInput,
  IMetricInput,
  ISignificanceExecuteInput,
  MetricTypeKey,
  QueryType,
  SignificanceModeKey,
  TimeUnit,
} from '../../../../../utils/apollo/graphqlSchema';
import { IBuilder } from '../../../../../redux/slices/oldBuilder';
import { assertCondition, assertNotNil } from '../../../../../utils/types';
import { parseCohortRowsToCohortRefInput } from '../../../../../utils/builder/cohorts';
import { parseFilterToInput } from '../../../../../utils/filterFields';
import {
  ISignificanceBuilderCriteria,
  ISignificanceBuilderMetric,
} from '../../../../../redux/slices/oldBuilder/significance/types';
import { IReplaceReport } from '../../../../../redux/slices/cachedEntities';
import { toDatePickerInput } from '../../../../../components/Builder/utils/builderUtils';
import { UnreachableCaseError } from 'ts-essentials';
import { llmChatMessageToInput } from '../../../../../components/Builder/utils/validateExecutionInput';

const prepareSignificanceCriteriaInput = (
  criteria: ISignificanceBuilderCriteria,
): ICriteriaInput => {
  const { modeKey } = criteria;

  switch (modeKey) {
    case SignificanceModeKey.Breakdown: {
      const { field, controlGroup, variants } = criteria;
      assertNotNil(field);
      assertNotNil(controlGroup);

      return {
        breakdownCriteria: {
          control: controlGroup,
          fieldKey: field.key,
          variants: variants.reduce<string[]>((acc, variant) => {
            if (!isNil(variant)) {
              acc.push(variant);
            }
            return acc;
          }, []),
        },
        criteriaMode: modeKey,
        experimentCriteria: null,
        segmentCriteria: null,
      };
    }
    case SignificanceModeKey.Segment: {
      const { controlGroup, variants } = criteria;
      assertNotNil(controlGroup);

      return {
        breakdownCriteria: null,
        criteriaMode: modeKey,
        experimentCriteria: null,
        segmentCriteria: {
          control: controlGroup.id,
          variants: variants.reduce<string[]>((acc, variant) => {
            if (!isNil(variant)) {
              acc.push(variant.id);
            }
            return acc;
          }, []),
        },
      };
    }
    case SignificanceModeKey.Experiment: {
      const { experimentId, controlGroup, variants } = criteria;
      assertNotNil(experimentId);
      assertNotNil(controlGroup);

      return {
        breakdownCriteria: null,
        criteriaMode: modeKey,
        experimentCriteria: {
          control: controlGroup,
          experiment: experimentId,
          variants: variants.reduce<string[]>((acc, variant) => {
            if (!isNil(variant)) {
              acc.push(variant);
            }
            return acc;
          }, []),
        },
        segmentCriteria: null,
      };
    }
    default:
      throw new UnreachableCaseError(modeKey);
  }
};
const prepareSignificanceMetricInput = (
  metric: ISignificanceBuilderMetric,
): IMetricInput => {
  const { type } = metric;

  switch (type) {
    case MetricTypeKey.Measure: {
      const { measure } = metric;
      assertNotNil(measure);

      return {
        funnelMetric: null,
        measureMetric: {
          measureId: measure.id,
        },
        metricType: type,
        queryMetric: null,
        retentionMetric: null,
      };
    }
    case MetricTypeKey.Query: {
      const { analysisId, measureId } = metric;
      assertNotNil(analysisId);
      assertNotNil(measureId);
      return {
        funnelMetric: null,
        measureMetric: null,
        metricType: type,
        queryMetric: {
          analysisId,
          measureId,
        },
        retentionMetric: null,
      };
    }
    case MetricTypeKey.Funnel: {
      const { analysisId, convertedOnStep, ttcMetric } = metric;
      assertNotNil(analysisId);

      return {
        funnelMetric: {
          analysisId,
          convertedOnStep,
          ttcMetric,
        },
        measureMetric: null,
        metricType: type,
        queryMetric: null,
        retentionMetric: null,
      };
    }
    case MetricTypeKey.Retention: {
      const { analysisId } = metric;
      assertNotNil(analysisId);

      return {
        funnelMetric: null,
        measureMetric: null,
        metricType: type,
        queryMetric: null,
        retentionMetric: {
          analysisId,
        },
      };
    }
    default: {
      throw new UnreachableCaseError(type);
    }
  }
};

interface IPrepareSignificanceInputArgs {
  builderState: IBuilder;
  replaceReport: IReplaceReport | null;
  analysisName?: string;
  analysisDescription?: string;
}

const prepareSignificanceInput = ({
  builderState,
  replaceReport,
  analysisName,
  analysisDescription,
}: IPrepareSignificanceInputArgs): ISignificanceExecuteInput => {
  const {
    activeSchemaId,
    globalCohorts,
    filter,
    selectedOptions,
    significance,
    sourceId,
    dateInput,
    llmContext,
  } = builderState;
  assertCondition(selectedOptions?.type === QueryType.Significance);
  const { subject, test } = selectedOptions;
  const { confidenceLevel, significanceLevel, metric, criteria } = significance;
  assertNotNil(criteria);
  assertNotNil(metric);

  return {
    cohortRefs: parseCohortRowsToCohortRefInput(globalCohorts, activeSchemaId),
    criteria: prepareSignificanceCriteriaInput(criteria),
    metric: prepareSignificanceMetricInput(metric),
    filter: filter ? parseFilterToInput(filter, activeSchemaId) : null,
    datePicker: toDatePickerInput(dateInput.datePicker),
    unit: TimeUnit.AllTime,
    confidenceLevel,
    samplePercentage: null,
    schemaId: activeSchemaId,
    significanceLevel,
    subject,
    sourceId: sourceId ?? null,
    test,
    display: replaceReport && analysisName ? analysisName : null,
    description:
      replaceReport && analysisDescription ? analysisDescription : null,
    llmChatMessages: llmContext.previousMessages.map(llmChatMessageToInput),
  };
};

export default prepareSignificanceInput;
